import { NETWORK_CONSTANTS } from '@/lib/constants/network'
import { combineAbortSignals } from '@/lib/fetchWithTimeout'
import {
  adjustTrackedFetchInFlight,
  setConnectivityGlobalSlow,
} from '@/lib/connectivity/store'

/** Request header: include on large non-Supabase payloads that should affect global slow state. */
export const CONNECTIVITY_TRACK_HEADER = 'x-clinic-ms-connectivity-track'

let localTrackedInFlight = 0
/** Browser timer handle (number in DOM; only used when `window` exists). */
let slowNoticeTimer: number | null = null

function isClientRuntime(): boolean {
  return typeof window !== 'undefined'
}

function scheduleGlobalSlowNotice() {
  if (!isClientRuntime() || slowNoticeTimer != null) return

  slowNoticeTimer = window.setTimeout(() => {
    slowNoticeTimer = null
    if (localTrackedInFlight > 0) {
      setConnectivityGlobalSlow(true)
    }
  }, NETWORK_CONSTANTS.SLOW_FETCH_NOTICE_MS)
}

function clearGlobalSlowNotice() {
  if (slowNoticeTimer != null) {
    window.clearTimeout(slowNoticeTimer)
    slowNoticeTimer = null
  }
  setConnectivityGlobalSlow(false)
}

function beginTrackedFetch() {
  if (!isClientRuntime()) return
  localTrackedInFlight += 1
  adjustTrackedFetchInFlight(1)
  if (localTrackedInFlight === 1) {
    scheduleGlobalSlowNotice()
  }
}

function endTrackedFetch() {
  if (!isClientRuntime()) return
  localTrackedInFlight = Math.max(0, localTrackedInFlight - 1)
  adjustTrackedFetchInFlight(-1)
  if (localTrackedInFlight === 0) {
    clearGlobalSlowNotice()
  }
}

function shouldTrackRequest(input: RequestInfo | URL, init?: RequestInit): boolean {
  if (init?.headers) {
    const headers = new Headers(init.headers)
    if (headers.get(CONNECTIVITY_TRACK_HEADER) === '1') return true
  }

  const url =
    typeof input === 'string'
      ? input
      : input instanceof URL
        ? input.href
        : input instanceof Request
          ? input.url
          : ''

  if (!url) return false

  // All Supabase HTTP traffic (PostgREST, Auth, Storage).
  if (/\.supabase\.co/i.test(url)) return true

  return false
}

/**
 * Supabase + explicitly marked large API `fetch` wrapper.
 * Applies network timeout and drives global in-flight / slow connectivity state on the client.
 */
export function connectivityFetch(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> {
  const timeoutSignal = AbortSignal.timeout(NETWORK_CONSTANTS.SUPABASE_FETCH_TIMEOUT_MS)
  const signal = init?.signal
    ? combineAbortSignals([init.signal, timeoutSignal])
    : timeoutSignal

  const track = shouldTrackRequest(input, init)
  if (track) beginTrackedFetch()

  return fetch(input, { ...init, signal }).finally(() => {
    if (track) endTrackedFetch()
  })
}

/** Mark a `fetch` init so {@link connectivityFetch} tracks it for global slow state. */
export function withConnectivityTracking(init?: RequestInit): RequestInit {
  const headers = new Headers(init?.headers)
  headers.set(CONNECTIVITY_TRACK_HEADER, '1')
  return { ...init, headers }
}

/** Test-only reset */
export function resetConnectivityFetchMiddlewareForTests() {
  localTrackedInFlight = 0
  if (slowNoticeTimer != null) {
    clearTimeout(slowNoticeTimer)
    slowNoticeTimer = null
  }
  setConnectivityGlobalSlow(false)
}
